import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useDebounceEffect, useMemoizedFn } from 'ahooks';
import { debounce, isEmpty } from 'lodash';
import { Loading } from 'tdesign-react';
import ShadowCard from '@/components/ShadowCard';
import {
  fetchRecordBasic,
  fetchRecordDetail,
  fetchRecordHost,
  resetRecordDetail,
  setCanRefresh,
} from '@/slices/execRecordSlice';
import { isVoid, sleep } from '@/utils/common';
import { abortRecordDetailRequest, abortRecordStepRequest } from '@/services/execRecordService';
import { RESULT_STATUS, getExecResultCodeStatus } from '..';
import ExecuteInfo from './ExecuteInfo';
import Servers from './Servers';
import Steps from './Steps';
import Summary from './Summary';

const timesOut = 3000;

function RecordDetail({
  fetchRecordBasic,
  fetchRecordHost,
  summary,
  detailLogVO,
  currentSubStep,
  currentStep,
  resetRecordDetail,
  currentHost,
  fetchRecordDetail,
  setCanRefresh,
  canRefresh,
}) {
  const [searchParams] = useSearchParams();
  const timeoutRef = useRef();
  const stepTimeoutRef = useRef();
  const [loading, setLoading] = useState(true);

  const id = searchParams.get('id');
  const clusterId = searchParams.get('clusterId');

  const doInit = useMemoizedFn(async () => {
    setLoading(true);
    await doStepRefresh(true);
    setLoading(false);
    // 初始化，开始步骤轮训
  });

  const getParams = useMemoizedFn(() => {
    const params = {
      id,
      stencilType: summary.stencilType,
      stencilId: summary.scriptId,
      clusterId,
    };
    if (summary.stencilType === 2) {
      Object.assign(params, { stage: currentSubStep?.stepId, flowStepId: currentStep.stepId });
    }
    if (summary.stencilType === 1) {
      Object.assign(params, { stage: currentSubStep?.stepId });
    }
    return params;
  });

  // 筛选详情
  const doDetailRefresh = useMemoizedFn(async (force) => {
    if (
      force ||
      (!isEmpty(currentHost) &&
        canRefresh &&
        getExecResultCodeStatus(detailLogVO.execResultCode) === RESULT_STATUS.PROCESSING)
    ) {
      try {
        await fetchRecordDetail({
          ...getParams(),
          hostId: currentHost.id,
        });
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        timeoutRef.current = setTimeout(doDetailRefresh, timesOut);
      }
    }
  });

  const doStepRefresh = useMemoizedFn(async (force) => {
    if (force || getExecResultCodeStatus(summary.execResultCode) === RESULT_STATUS.PROCESSING) {
      try {
        await fetchRecordBasic({ id, clusterId });
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        stepTimeoutRef.current = setTimeout(doStepRefresh, timesOut);
      }
      // 执行中
    }
  });

  // 1秒后刷新并尝试重新轮训
  const executeRefresh = debounce(async () => {
    await doCleanStep();
    doStepRefresh(true);
    await reloadDetail();
  }, 1000);

  // 一级目录选中
  const parentStepId = useMemo(() => {
    return currentStep?.stepId;
  }, [currentStep]);

  // 退出页面，保障初始化
  useEffect(() => {
    return () => {
      resetRecordDetail();
    };
  }, [resetRecordDetail]);

  // 监听步骤刷新机器
  useEffect(() => {
    if (isVoid(parentStepId)) {
      return;
    }
    abortRecordDetailRequest();
    const params = getParams();
    fetchRecordHost(params);
  }, [fetchRecordHost, parentStepId, getParams]);

  const reloadDetail = useMemoizedFn(async () => {
    await doCleanDetail();
    setCanRefresh(true);
    doDetailRefresh(true);
  });

  // 节点、机器选中发生变化
  useDebounceEffect(() => {
    if (isEmpty(currentHost)) {
      return;
    }
    reloadDetail();
  }, [
    currentStep,
    currentSubStep,
    currentHost,
    fetchRecordDetail,
    getParams,
    setCanRefresh,
    doDetailRefresh,
    reloadDetail,
  ]);

  // 详情清除轮询
  const doCleanDetail = async () => {
    // 立刻停止
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    // 中止
    abortRecordDetailRequest();

    // 再次保证，防止Finnally中继续执行
    await sleep(10);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  // 清除步骤轮询
  const doCleanStep = async () => {
    // 立刻停止
    if (stepTimeoutRef.current) {
      clearTimeout(stepTimeoutRef.current);
      stepTimeoutRef.current = null;
    }
    // 中止
    abortRecordStepRequest();

    // 再次保证，防止Finnally中继续执行
    await sleep(10);
    if (stepTimeoutRef.current) {
      clearTimeout(stepTimeoutRef.current);
      stepTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    doInit();
    return () => {
      doCleanStep();
      doCleanDetail();
    };
  }, [doInit]);

  return (
    <Loading loading={loading}>
      <Root>
        {!loading && (
          <>
            <Summary id={id} executeRefresh={executeRefresh} />
            <ShadowCard className='body-panel'>
              <Steps />
              <Vl />
              <Servers />
              <Vl />
              <ExecuteInfo />
            </ShadowCard>
          </>
        )}
      </Root>
    </Loading>
  );
}

export default connect(
  (state) => ({
    summary: state.execRecord.summary,
    detailLogVO: state.execRecord.detailLogVO,
    currentStep: state.execRecord.currentStep,
    currentSubStep: state.execRecord.currentSubStep,
    currentHost: state.execRecord.currentHost,
    canRefresh: state.execRecord.canRefresh,
  }),
  {
    fetchRecordBasic,
    fetchRecordHost,
    fetchRecordDetail,
    resetRecordDetail,
    setCanRefresh,
  },
)(RecordDetail);

const Root = styled.div`
  min-height: calc(100vh - 140px);
  margin-right: 20px;
  .body-panel .shadow-card-body {
    padding: 0;
    min-height: 100px;
    display: flex;
  }
`;

const Vl = styled.div`
  background: #e7e7e7;
  width: 1px;
`;
